import { useMemo, useCallback } from 'react';

function WaterRatePage({ hauls, wellName, tankLabel, bblPerIn }) {
  // post_slope_ft_per_hr is actually in inches/hr despite the _ft suffix.
  // water_rate = slope (in/hr) × bblPerIn × 24 (hr/day)
  const rows = useMemo(() => {
    return hauls
      .filter(h => h.post_slope_ft_per_hr != null && h.post_slope_ft_per_hr > 0)
      .map(h => {
        const waterRate = h.post_slope_ft_per_hr * bblPerIn * 24;
        const gasRate = h.gas_rate_mcf_day ?? null;
        const wgr = gasRate > 0 ? waterRate / gasRate : null;
        const haulBbl = h.haul_volume_ft != null && bblPerIn > 0
          ? h.haul_volume_ft * bblPerIn : null;
        return {
          drop_start: h.drop_start,
          drop_end: h.drop_end,
          fillRate: h.post_slope_ft_per_hr,
          waterRate,
          gasRate,
          wgr,
          haulBbl,
        };
      });
  }, [hauls, bblPerIn]);

  const summary = useMemo(() => {
    if (rows.length === 0) return null;
    const avgWR = rows.reduce((s, r) => s + r.waterRate, 0) / rows.length;
    const gasRows = rows.filter(r => r.gasRate > 0);
    const avgGas = gasRows.length > 0
      ? gasRows.reduce((s, r) => s + r.gasRate, 0) / gasRows.length : null;
    const wgrRows = rows.filter(r => r.wgr != null);
    const avgWgr = wgrRows.length > 0
      ? wgrRows.reduce((s, r) => s + r.wgr, 0) / wgrRows.length : null;
    return { avgWR, avgGas, avgWgr, count: rows.length };
  }, [rows]);

  const downloadCsv = useCallback(() => {
    const cols = [
      { label: 'Well', accessor: () => wellName || '' },
      { label: 'Drop Start', accessor: r => r.drop_start },
      { label: 'Drop End', accessor: r => r.drop_end },
      { label: 'Fill Rate (in/hr)', accessor: r => r.fillRate?.toFixed(4) ?? '' },
      { label: 'Water Rate (bwpd)', accessor: r => r.waterRate?.toFixed(1) ?? '' },
      { label: 'Gas Rate (mcf/d)', accessor: r => r.gasRate?.toFixed(1) ?? '' },
      { label: 'WGR (bbl/mcf)', accessor: r => r.wgr?.toFixed(3) ?? '' },
      { label: 'Haul Volume (bbl)', accessor: r => r.haulBbl?.toFixed(1) ?? '' },
    ];
    const header = cols.map(c => c.label).join(',');
    const body = rows.map(r => cols.map(c => {
      const val = c.accessor(r);
      if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
      return val ?? '';
    }).join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = (wellName || 'well').replace(/[^a-zA-Z0-9_-]/g, '_');
    a.download = `${safeName}_${tankLabel}_water_rate.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, wellName, tankLabel]);

  return (
    <div>
      <div className="data-page-header">
        <div className="data-page-tabs">
          <span className="wr-summary">
            {summary ? (
              <>
                <strong>{rows.length}</strong> hauls with fill data
                {' · '}Avg <strong>{summary.avgWR.toFixed(1)} bwpd</strong>
                {summary.avgGas != null && (<>{' · '}Gas <strong>{summary.avgGas.toFixed(1)} mcf/d</strong></>)}
                {summary.avgWgr != null && (<>{' · '}WGR <strong>{summary.avgWgr.toFixed(3)} bbl/mcf</strong></>)}
              </>
            ) : 'No hauls with valid fill slope'}
          </span>
        </div>
        <button className="quick-btn" onClick={downloadCsv} disabled={rows.length === 0}>
          Download CSV
        </button>
      </div>

      <div className="haul-table-card">
        <div className="haul-table-wrap" style={{ maxHeight: 500 }}>
          <table>
            <thead>
              <tr>
                <th>Drop Start</th>
                <th className="num">Fill Rate (in/hr)</th>
                <th className="num">Water Rate (bwpd)</th>
                <th className="num">Gas Rate (mcf/d)</th>
                <th className="num">WGR (bbl/mcf)</th>
                <th className="num">Haul Volume (bbl)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{new Date(r.drop_start).toLocaleString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit'
                  })}</td>
                  <td className="num">{r.fillRate.toFixed(4)}</td>
                  <td className="num">{r.waterRate.toFixed(1)}</td>
                  <td className="num">{r.gasRate != null ? r.gasRate.toFixed(1) : '—'}</td>
                  <td className="num">{r.wgr != null ? r.wgr.toFixed(3) : '—'}</td>
                  <td className="num">{r.haulBbl != null ? r.haulBbl.toFixed(1) : '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                  No hauls with valid fill slope data
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default WaterRatePage;
